/**
 * =============================================================================
 * IDS CRM Web - A bulletin read in place, over the form that found it
 * =============================================================================
 *
 * Opened from the bulletins listed under Fault codes in Log Work (engineer
 * bulletins Phase 3). Read IN PLACE rather than in a new tab (review
 * 2026-09-14): from IDS CRM on an iPhone home screen a new tab opens in
 * Safari, which does not share the app's sign-in, so the engineer met the
 * login page and then the dashboard, never the fix. In place, the
 * half-written log also stays exactly as it was underneath.
 *
 * Files still open in a new tab: their links carry their own access token
 * (utils/files.js authedFileHref), so they open without the app's sign-in.
 * The text is plain text (pre-wrap), never HTML.
 * =============================================================================
 */

import React, { useEffect, useRef, useState } from 'react';
import { authedFileHref } from '../utils/files';
import { formatDate } from '../utils/format';
import { formatFileSize } from '../utils/bulletins';
import { BulletinStatusBadge, FaultCodeChips } from './BulletinParts';
import './BulletinPeek.css';

function BulletinPeek({ bulletinId, authFetch, onClose }) {
  // Following "replaced by" stays inside the viewer.
  const [shownId, setShownId] = useState(bulletinId);
  const [state, setState] = useState({ loading: true, bulletin: null, error: '' });
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, bulletin: null, error: '' });
    (async () => {
      try {
        const response = await authFetch(`/api/v1/bulletins/${shownId}`);
        const body = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          setState({
            loading: false,
            bulletin: null,
            error: response.status === 404
              ? "This bulletin isn't available any more."
              : (body.message || 'Could not load this bulletin. Try again.')
          });
          return;
        }
        setState({ loading: false, bulletin: body.data || null, error: '' });
      } catch (err) {
        if (!cancelled) {
          setState({ loading: false, bulletin: null, error: 'Could not load this bulletin. Check your signal and try again.' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, shownId]);

  useEffect(() => {
    if (closeRef.current) closeRef.current.focus();
  }, []);

  // Escape closes the viewer only; the log form behind it stays open. Tab stays
  // inside the viewer, so it cannot wander into the form underneath.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll('button:not([disabled]), a[href]'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const { loading, bulletin, error } = state;
  const attachments = bulletin && Array.isArray(bulletin.attachments) ? bulletin.attachments : [];

  return (
    <div className="bulletin-peek-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className="bulletin-peek"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulletin-peek-title"
        onClick={event => event.stopPropagation()}
      >
        <div className="bulletin-peek-header">
          <h2 id="bulletin-peek-title">{bulletin ? bulletin.title : 'Bulletin'}</h2>
          <button ref={closeRef} type="button" className="bulletin-peek-close" onClick={onClose}>
            Back to the log
          </button>
        </div>

        <div className="bulletin-peek-body">
          {loading && <p className="bulletin-peek-note">Loading…</p>}
          {error && <p className="bulletin-peek-error" role="alert">{error}</p>}

          {bulletin && (
            <>
              {bulletin.status === 'superseded' && bulletin.superseded_by_bulletin_id && (
                <p className="bulletin-peek-banner">
                  This bulletin has been replaced by a newer one.{' '}
                  <button
                    type="button"
                    className="bulletin-peek-inline"
                    onClick={() => setShownId(bulletin.superseded_by_bulletin_id)}
                  >
                    {`Read ${bulletin.superseded_by_title ? `'${bulletin.superseded_by_title}'` : 'the newer bulletin'}`}
                  </button>
                </p>
              )}

              <div className="bulletin-peek-meta">
                <BulletinStatusBadge bulletin={bulletin} />
                <span>
                  {bulletin.published_at ? `Published ${formatDate(bulletin.published_at)}` : ''}
                  {bulletin.created_by_name ? ` · written by ${bulletin.created_by_name}` : ''}
                </span>
              </div>

              <FaultCodeChips codes={bulletin.fault_codes} />

              <div className="bulletin-peek-text">{bulletin.body}</div>

              {attachments.length > 0 && (
                <ul className="bulletin-peek-files">
                  {attachments.map((file, index) => (
                    <li key={file.attachment_id || index}>
                      <a href={authedFileHref(file.url)} target="_blank" rel="noreferrer">
                        {file.name || `File ${index + 1}`}
                      </a>
                      {formatFileSize(file.size) && (
                        <span className="bulletin-peek-file-size">{formatFileSize(file.size)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulletinPeek;
